import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowsClockwise } from "@phosphor-icons/react";
import api, { describeApiError, isCancelled, referenceIdFor } from "@/lib/api";
import useAbortSignal from "@/lib/useAbortSignal";
import { APP_VERSION } from "@/version";

/**
 * System Admin → System Health (platform admin only).
 *
 * Every component is judged independently by the backend under its own
 * timeout, so one broken subsystem shows as one red/amber row — never a
 * blank page. Read-only: there is deliberately no "fix" button here;
 * links go to the existing admin page for that subsystem.
 */
const STATE_STYLE = {
  HEALTHY: { bg: "#ECFDF5", fg: "#047857", label: "Healthy" },
  DEGRADED: { bg: "#FFFBEB", fg: "#B45309", label: "Degraded" },
  FAILED: { bg: "#FEF2F2", fg: "#B91C1C", label: "Failed" },
  UNKNOWN: { bg: "#F5F5F5", fg: "#525252", label: "Unknown" },
  OUTAGE: { bg: "#FEF2F2", fg: "#B91C1C", label: "Outage" },
};

const LINK_LABELS = {
  "/admin?tab=backups": "View Backups",
  "/marketing/health": "View Marketing Health",
  "/marketing/activity": "View Email Activity",
  "/crm/leads": "Open Sales Leads",
  "/admin?tab=config": "View System Admin",
};

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function Badge({ state }) {
  const s = STATE_STYLE[state] || STATE_STYLE.UNKNOWN;
  return (
    <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
          style={{ background: s.bg, color: s.fg }} data-testid={`health-state-${state}`}>
      {s.label}
    </span>
  );
}

export default function SystemHealthTab() {
  const navigate = useNavigate();
  const nextSignal = useAbortSignal();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (refresh = false) => {
    const signal = nextSignal();
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/system-health${refresh ? "?refresh=true" : ""}`,
        { signal, timeout: 30000 });
      setReport(r.data);
    } catch (e) {
      if (isCancelled(e)) return;
      setError({ message: describeApiError(e), referenceId: referenceIdFor(e, "HEALTH") });
    }
    if (!signal.aborted) setLoading(false);
  }, [nextSignal]);

  useEffect(() => { load(false); }, [load]);

  const overall = report?.overall;
  const os = STATE_STYLE[overall] || STATE_STYLE.UNKNOWN;
  const frontendRow = {
    key: "frontend_bundle", name: "Frontend (this browser)", state: "HEALTHY",
    reason: `Running build ${APP_VERSION}.`, latency_ms: null,
    last_success_at: new Date().toISOString(), last_checked_at: new Date().toISOString(),
  };

  return (
    <div className="space-y-4" data-testid="system-health-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap border p-4"
           style={{ borderColor: os.fg + "55", background: os.bg }}>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">System health</div>
          <div className="text-2xl font-black tracking-tight mt-1" style={{ color: os.fg }}
               data-testid="system-health-overall">
            {overall ? os.label.toUpperCase() : loading ? "CHECKING…" : "UNAVAILABLE"}
          </div>
          <div className="text-xs text-[#525252] mt-1">
            Last refreshed: {fmt(report?.generated_at)} · Backend version: {report?.version || "—"}
            {report?.build_sha ? ` (${report.build_sha})` : ""} · Environment: {report?.environment || "—"}
          </div>
        </div>
        <button onClick={() => load(true)} disabled={loading}
          className="inline-flex items-center gap-1.5 h-9 px-4 text-xs font-bold uppercase tracking-wider bg-[#0055FF] text-white hover:bg-[#0044CC] disabled:opacity-50"
          data-testid="system-health-refresh">
          <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          {loading ? "Checking…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-sm" data-testid="system-health-error">
          <b>System health could not be loaded.</b> {error.message}
          <div className="text-xs text-[#737373] mt-1">Reference: {error.referenceId}</div>
          <button onClick={() => load(true)} className="mt-2 h-8 px-3 text-xs font-bold uppercase tracking-wider bg-[#0055FF] text-white">Retry</button>
        </div>
      )}

      {report && (
        <div className="border border-[#E5E5E5] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-[0.15em] text-[#525252]">
              <tr>
                <th className="text-left px-4 py-2">Component</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Reason</th>
                <th className="text-left px-4 py-2">Last success</th>
                <th className="text-right px-4 py-2">Latency</th>
                <th className="text-left px-4 py-2">Last checked</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F5F5]">
              {[frontendRow, ...report.components].map((c) => (
                <tr key={c.key} data-testid={`health-row-${c.key}`}>
                  <td className="px-4 py-2.5 font-bold text-[#0A0A0A] whitespace-nowrap">{c.name}</td>
                  <td className="px-4 py-2.5"><Badge state={c.state} /></td>
                  <td className="px-4 py-2.5 text-[#525252]">
                    {c.reason}
                    {c.link && LINK_LABELS[c.link] && (
                      <button onClick={() => navigate(c.link)}
                        className="ml-2 text-xs font-bold text-[#0055FF] hover:underline">{LINK_LABELS[c.link]}</button>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-[#525252] whitespace-nowrap">{fmt(c.last_success_at)}</td>
                  <td className="px-4 py-2.5 text-xs text-right tabular-nums">{c.latency_ms != null ? `${c.latency_ms} ms` : "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-[#525252] whitespace-nowrap">{fmt(c.last_checked_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-[#A3A3A3]">
        Each check runs independently with its own time limit; a check that times out shows as
        Unknown rather than holding up the page. The public status page shows a simplified view
        without these details.
      </p>
    </div>
  );
}
